import { StyleSheet, View } from 'react-native'

import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Text, useTheme } from '../theme/restyle'
import { FiatChoiceField } from './FiatChoiceField'
import { FiatChoiceList } from './FiatChoiceList'
import { FiatKycPhoneField } from './FiatKycPhoneField'
import { PHONE_COUNTRIES, countryFlag, countryName } from './phoneCountries'
import { FiatBirthDateField } from './FiatBirthDateField'
import {
  FiatFormFieldRow,
  type FiatFormFieldRowProps,
} from './FiatFormFieldRow'
import { FiatSsnField } from './FiatSsnField'
import type { NativeDatePicker } from './nativeDatePicker'

interface KycFieldControlProps extends FiatFormFieldRowProps {
  ssnDigits?: 4 | 9
  /** The buyer's region, which the phone field's calling code opens on. */
  defaultCountry?: string
  Picker?: NativeDatePicker
  /** The step's frame, which the wheel centres itself on. */
  frame?: { height: number; top: number }
  isDatePickerLoading?: boolean
  /** The page draws its answers itself, rather than behind a chooser. */
  inlineChoice?: boolean
}

export function FiatKycFieldControl({
  caption,
  ...props
}: KycFieldControlProps & {
  /** Named above the control, where the page's own line does not name it. */
  caption?: string
}) {
  const theme = useTheme()
  const control = <KycFieldControl {...props} />
  if (caption === undefined) {
    return control
  }
  return (
    <View style={styles.captioned}>
      <Text
        weight="medium"
        style={[styles.caption, { color: theme.content.elements.secondary }]}
      >
        {caption}
      </Text>
      {control}
    </View>
  )
}

function KycFieldControl({
  ssnDigits,
  Picker,
  frame,
  isDatePickerLoading = false,
  inlineChoice = false,
  defaultCountry,
  ...props
}: KycFieldControlProps) {
  const { t, locale } = useFunkitTranslation()
  if (isDatePickerLoading) {
    return null
  }
  if (props.field.id === 'PHONE_NUMBER') {
    return (
      <FiatKycPhoneField
        value={props.value}
        defaultCountry={defaultCountry}
        editable={props.editable ?? true}
        autoFocus={props.autoFocus ?? false}
        returnKey={props.returnKey}
        inputRef={props.inputRef}
        onChangeValue={props.onChangeValue}
        onBlur={props.onBlur}
      />
    )
  }
  if (props.field.type === 'COUNTRY') {
    return (
      <FiatChoiceField
        label={props.field.labelFallback}
        title={t('fiat.kycCountry')}
        value={props.value}
        options={PHONE_COUNTRIES.map((country) => ({
          id: country.alpha2,
          labelFallback: `${countryFlag(country.alpha2)} ${countryName(country, locale)}`,
        }))}
        editable={props.editable ?? true}
        invalid={props.issue !== undefined}
        grouped={props.grouped}
        country
        onChange={props.onChangeValue}
      />
    )
  }
  if (props.field.type === 'SELECT' && inlineChoice) {
    return (
      <FiatChoiceList
        value={props.value}
        options={props.field.options}
        editable={props.editable ?? true}
        invalid={props.issue !== undefined}
        onChange={props.onChangeValue}
      />
    )
  }
  if (props.field.type === 'SELECT') {
    let title = props.field.labelFallback
    if (
      props.field.id === 'OCCUPATION_TITLE' ||
      props.field.id === 'OCCUPATION_INDUSTRY'
    ) {
      title = t('fiat.kycOccupation')
    }
    if (
      props.field.id === 'PURPOSE_OF_TRANSACTION' ||
      props.field.id === 'PURPOSE_OF_SERVICE'
    ) {
      title = t('fiat.kycPurpose')
    }
    return (
      <FiatChoiceField
        label={props.field.labelFallback}
        title={title}
        value={props.value}
        options={props.field.options}
        editable={props.editable ?? true}
        invalid={props.issue !== undefined}
        grouped={props.grouped}
        onChange={props.onChangeValue}
      />
    )
  }
  if (ssnDigits !== undefined) {
    return (
      <FiatSsnField
        value={props.value}
        length={ssnDigits}
        label={props.field.labelFallback}
        editable={props.editable ?? true}
        autoFocus={props.autoFocus ?? false}
        invalid={props.issue !== undefined}
        inputRef={props.inputRef}
        onChange={props.onChangeValue}
        onBlur={props.onBlur}
      />
    )
  }
  if (Picker !== undefined) {
    return (
      <FiatBirthDateField
        Picker={Picker}
        value={props.value}
        frameHeight={frame?.height ?? 0}
        frameTop={frame?.top ?? 0}
        editable={props.editable ?? true}
        onChange={props.onChangeValue}
      />
    )
  }
  return <FiatFormFieldRow {...props} />
}

const styles = StyleSheet.create({
  captioned: { gap: 6 },
  caption: {
    paddingHorizontal: 16,
    fontSize: 13,
    lineHeight: 16,
    letterSpacing: -0.26,
  },
})
